import { Alert, Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { FC } from 'react';
import styled from 'styled-components';
import { UserInfo } from '../../../common/model/dto/user-info';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import { getLatestOvulatoryEventPrediction } from '../../../services/admin/admin-ovulatory-event-prediction.service';
import OvOvulatoryEventEmptyState from '../molecules/OvOvulatoryEventEmptyState';
import OvOvulatoryEventSurface from '../molecules/OvOvulatoryEventSurface';
import OvOvulatoryEventComparison from './OvOvulatoryEventComparison';
import { FetchDailyData } from './OvUserCharts';

interface OvOvulatoryEventPredictionProps {
  selectedUser?: UserInfo;
  fetchDailyData: FetchDailyData;
  fetchCalendarDailyData: FetchDailyData;
}

const OvOvulatoryEventPrediction: FC<OvOvulatoryEventPredictionProps> = ({
  selectedUser,
  fetchDailyData,
  fetchCalendarDailyData,
}) => {
  const userId = selectedUser?.id;

  const latestQuery = useQuery({
    queryKey: ['admin-ovulatory-event-latest-prediction', userId],
    queryFn: () => getLatestOvulatoryEventPrediction(userId ?? ''),
    enabled: !!userId,
  });

  return (
    <Container>
      <Header>
        <Title>Ovulatory event prediction</Title>
        <Meta>Model 1A · Internal only · Read-only</Meta>
      </Header>

      {latestQuery.isPending && (
        <OvOvulatoryEventSurface>
          <Skeleton
            variant="rounded"
            height="6rem"
            role="status"
            aria-label="Loading predictions"
          />
        </OvOvulatoryEventSurface>
      )}

      {latestQuery.isError && (
        <StyledAlert severity="warning">
          Could not check whether this member has any predictions (
          {latestQuery.error.message}). The history below is loaded separately.
        </StyledAlert>
      )}

      {latestQuery.data === null && (
        <OvOvulatoryEventSurface>
          <OvOvulatoryEventEmptyState title="No predictions for this member yet">
            <p>
              No scored days are available. A prediction is written when the
              member scans PdG after the model is deployed to this environment,
              or when the one-off backfill covers their recent scans.
            </p>
          </OvOvulatoryEventEmptyState>
        </OvOvulatoryEventSurface>
      )}

      {userId && latestQuery.data !== null && !latestQuery.isPending && (
        <OvOvulatoryEventComparison
          userId={userId}
          fetchDailyData={fetchDailyData}
          fetchCalendarDailyData={fetchCalendarDailyData}
        />
      )}
    </Container>
  );
};

export default OvOvulatoryEventPrediction;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  margin-bottom: 2rem;
`;

const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.25rem 1.5rem;
`;

const Title = styled.h2`
  margin: 0;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MLARGE};
  font-weight: bold;
`;

const Meta = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const StyledAlert = styled(Alert)`
  && {
    border-radius: ${Variables.borderRadius.LARGE};
  }
`;
